import axios from "axios";
import { useContext, useEffect, useState } from "react";
import {
  X,
  User,
  Ticket,
  FileText,
  PartyPopper,
  Loader2,
  Lock,
  Unlock,
  Plus,
  Check,
  Folder,
  Receipt,
  Pencil,
  ExternalLink,
  Save,
  LayoutGrid,
  Plane,
  Mail,
  ReceiptText,
} from "lucide-react";
import { base_url } from "../../config";
import {
  getTutorialDaysString,
  getWorkshopDaysString,
} from "../../util/ijcaihelpers";
import RegistrationContext from "../../contexts/RegistrationContext";

const typeChip = (value) => {
  switch (value) {
    case "industry":      return { label: "Industry day",      cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
    case "regular":       return { label: "Regular delegate",  cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" };
    case "student":       return { label: "Student",           cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
    case "student-paper": return { label: "Student with paper", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" };
    case "exhibitor":     return { label: "Exhibitor",         cls: "bg-orange-500/15 text-orange-300 border-orange-500/30" };
    default:              return { label: value || "—",        cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" };
  }
};

const Layout = ({ children, onClose, width = "80vw", maxWidth = "1100px", height = "85vh" }) => (
  <div
    className="dark fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    onClick={onClose}
  >
    <div
      className="relative flex flex-col bg-slate-900 text-slate-100 border border-slate-700/50 rounded-2xl shadow-2xl"
      style={{ width, maxWidth, height, overflow: "hidden" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="absolute top-3 right-3 inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700/50 transition-colors z-10"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      {children}
    </div>
  </div>
);

const Loading = ({ onClose }) => (
  <Layout onClose={onClose}>
    <div className="flex items-center justify-center h-full">
      <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
    </div>
  </Layout>
);

const Section = ({ icon, title, children }) => (
  <section className="rounded-xl border border-slate-700/40 bg-slate-800/40 overflow-hidden">
    <header className="flex items-center gap-2 px-4 py-2 border-b border-slate-700/40 bg-slate-900/40">
      <span className="text-slate-400">{icon}</span>
      <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">
        {title}
      </h3>
    </header>
    <div className="p-4 space-y-1.5 text-sm">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-[11px] uppercase tracking-wider text-slate-400 shrink-0">
      {label}
    </span>
    <span className="text-slate-100 text-right truncate">
      {children ?? <span className="text-slate-500">—</span>}
    </span>
  </div>
);

const RailButton = ({ icon, label, onClick, active = false, accent = "slate", disabled = false }) => {
  const inactive = "text-slate-300 border-slate-700/40 hover:bg-slate-700/40";
  const activeBy = {
    slate:   "text-slate-100 border-slate-500/40 bg-slate-700/40",
    blue:    "text-blue-200 border-blue-500/40 bg-blue-500/20",
    emerald: "text-emerald-200 border-emerald-500/40 bg-emerald-500/20",
    amber:   "text-amber-200 border-amber-500/40 bg-amber-500/20",
    violet:  "text-violet-200 border-violet-500/40 bg-violet-500/20",
    rose:    "text-rose-200 border-rose-500/40 bg-rose-500/20",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        active ? activeBy[accent] : inactive
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate text-left">{label}</span>
    </button>
  );
};

const moneyFormat = (amount, currency) => {
  if (amount === null || amount === undefined) return "—";
  const cur = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(amount);
  } catch {
    return `${amount} ${cur}`;
  }
};

const MAIN_LINE_BY_TYPE_ACCESS = {
  "regular-full": "Regular delegate main conference registration",
  "student-full": "Student main conference registration",
  "regular-workshops": "Regular delegate workshop-only registration",
  "student-workshops": "Student delegate workshop-only registration",
  "student-paper-full": "Student main conference registration with paper",
  "industry-full": "Industry day registration",
  "exhibitor-full": "Exhibitor registration",
};

const deriveInvoiceItems = (inv, extra, prices) => {
  const eventTickets = prices?.event_tickets;
  const invitationLetterPrice = Number(prices?.invitation_letter ?? 0);
  if (inv.invoice_details) {
    let parsed = inv.invoice_details;
    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        parsed = null;
      }
    }
    if (Array.isArray(parsed)) {
      return parsed
        .filter((d) => d && (d.detail || d.price))
        .map((d) => ({ label: d.detail, price: Number(d.price) || 0 }));
    }
  }

  const items = [];
  if (inv.base_price != null) {
    const key = `${extra?.type ?? ""}-${extra?.access ?? ""}`;
    const label = MAIN_LINE_BY_TYPE_ACCESS[key] || "Conference registration";
    items.push({ label, price: Number(inv.base_price) });
  }
  if (inv.workshops_price && inv.workshops_price > 0) {
    items.push({ label: "Workshops and tutorials", price: Number(inv.workshops_price) });
  }
  if (inv.invitation_letter) {
    items.push({ label: "Invitation letter", price: invitationLetterPrice });
  }
  if (inv.invitation_letter_montreal) {
    items.push({ label: "Invitation letter (Montreal)", price: invitationLetterPrice });
  }
  if (inv.invitation_letter_guangzhou) {
    items.push({ label: "Invitation letter (Guangzhou)", price: invitationLetterPrice });
  }
  if (inv.extra_banquet > 0) {
    items.push({ label: `${inv.extra_banquet} extra banquet ticket${inv.extra_banquet > 1 ? "s" : ""}`, price: inv.extra_banquet * (eventTickets?.banquet ?? 0) });
  }
  if (inv.extra_reception > 0) {
    items.push({ label: `${inv.extra_reception} extra reception ticket${inv.extra_reception > 1 ? "s" : ""}`, price: inv.extra_reception * (eventTickets?.reception ?? 0) });
  }
  if (inv.extra_student > 0) {
    items.push({ label: `${inv.extra_student} extra student reception ticket${inv.extra_student > 1 ? "s" : ""}`, price: inv.extra_student * (eventTickets?.student_reception ?? 0) });
  }
  if (inv.diversity_inclusion > 0) {
    items.push({ label: `${inv.diversity_inclusion} diversity & inclusion lunch ticket${inv.diversity_inclusion > 1 ? "s" : ""}`, price: inv.diversity_inclusion * (eventTickets?.diversity_inclusion ?? 0) });
  }
  if (inv.discount_price > 0) {
    items.push({ label: "Discount", price: -Number(inv.discount_price) });
  }
  return items;
};

const BILLING_FIELDS = [
  { key: "billing_address1", label: "Address line 1", col: 2 },
  { key: "billing_address2", label: "Address line 2", col: 2 },
  { key: "billing_postcode", label: "Postcode", col: 1 },
  { key: "billing_city", label: "City", col: 1 },
  { key: "billing_province", label: "Province / State", col: 1 },
  { key: "billing_country", label: "Country", col: 1 },
  { key: "billing_tax_id", label: "Tax ID", col: 2 },
];

// ---------- Overview view ----------
const OverviewView = ({ basic, extra, user }) => {
  const socialPrefs = extra.social_event_preferences || [];
  const receptionPrefs = socialPrefs.filter((i) => i.event === "reception");
  const studentReceptionPrefs = socialPrefs.filter((i) => i.event === "student_reception");
  const banquetPrefs = socialPrefs.filter((i) => i.event === "gala_dinner");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section icon={<User className="h-4 w-4" />} title="Personal">
          <Field label="Name">
            <b>{`${extra.first_name ?? ""} ${extra.family_name ?? ""}`.trim() || "—"}</b>
          </Field>
          <Field label="Affiliation">
            <b>{extra.affiliation || "—"}</b>
          </Field>
          {extra.affilition2 && (
            <Field label="Affiliation 2">
              <b>{extra.affilition2}</b>
            </Field>
          )}
          <Field label="Email">
            <b>{user.email || "—"}</b>
          </Field>
        </Section>

        <Section icon={<Ticket className="h-4 w-4" />} title="Registration">
          <Field label="Internal id">
            <span className="text-slate-400">#{basic.id}</span>
          </Field>
          <Field label="Tutorial days">
            <b>{getTutorialDaysString(extra) || "—"}</b>
          </Field>
          <Field label="Workshop days">
            <b>{getWorkshopDaysString(extra) || "—"}</b>
          </Field>
        </Section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section icon={<FileText className="h-4 w-4" />} title="Papers & sessions">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Papers registered</div>
            {basic.papers && basic.papers.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {basic.papers.map((paper) => (
                  <span
                    key={paper.paper_id}
                    className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-100 border border-slate-600/40"
                  >
                    {paper.paper_id}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs">No papers selected.</p>
            )}
          </div>

          <div className="pt-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Workshops</div>
            {extra.workshops && extra.workshops.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {extra.workshops.map((w) => (
                  <span
                    key={w.workshop_code}
                    className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-violet-500/15 text-violet-200 border border-violet-500/30"
                  >
                    {w.workshop_code}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs">No workshops selected.</p>
            )}
          </div>

          <div className="pt-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Tutorials</div>
            {extra.tutorials && extra.tutorials.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {extra.tutorials.map((t) => (
                  <span
                    key={t.tutorial_code}
                    className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-200 border border-amber-500/30"
                  >
                    {t.tutorial_code}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs">No tutorials selected.</p>
            )}
          </div>
        </Section>

        <Section icon={<PartyPopper className="h-4 w-4" />} title="Social events">
          <Field label={`Reception (${receptionPrefs.length})`}>
            <b>{receptionPrefs.map((i) => i.choice).join(", ") || "—"}</b>
          </Field>
          <Field label={`Student reception (${studentReceptionPrefs.length})`}>
            <b>{studentReceptionPrefs.map((i) => i.choice).join(", ") || "—"}</b>
          </Field>
          <Field label={`Banquet (${banquetPrefs.length})`}>
            <b>{banquetPrefs.map((i) => i.choice).join(", ") || "—"}</b>
          </Field>
          <Field label="Diversity & inclusion">
            <b>{extra.diversity_and_inclusion > 0 ? extra.diversity_and_inclusion : "No"}</b>
          </Field>
        </Section>
      </div>
    </div>
  );
};

// ---------- Documents view ----------
const docVisual = (kind) => {
  switch (kind) {
    case "invoice":           return { icon: <FileText  className="h-4 w-4" />, cls: "bg-blue-500/15 text-blue-300" };
    case "receipt":           return { icon: <ReceiptText className="h-4 w-4" />, cls: "bg-emerald-500/15 text-emerald-300" };
    case "invitation_letter": return { icon: <Mail      className="h-4 w-4" />, cls: "bg-violet-500/15 text-violet-300" };
    case "visa_letter":       return { icon: <Plane     className="h-4 w-4" />, cls: "bg-rose-500/15 text-rose-300" };
    default:                  return { icon: <FileText  className="h-4 w-4" />, cls: "bg-slate-500/15 text-slate-300" };
  }
};

const DocumentsView = ({ documents }) => {
  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${base_url}${doc.download_url}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Error downloading document:", err);
    }
  };

  return (
    <Section icon={<Folder className="h-4 w-4" />} title={`Documents (${documents.length})`}>
      {documents.length === 0 ? (
        <p className="text-sm text-slate-400">No documents on file for this registration.</p>
      ) : (
        <div className="space-y-2">
          {documents.map((doc, i) => {
            const v = docVisual(doc.kind);
            return (
              <div
                key={`${doc.kind}-${doc.type ?? ""}-${i}`}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-700/40 bg-slate-900/40"
              >
                <span className={`inline-flex items-center justify-center h-9 w-9 rounded-md ${v.cls}`}>
                  {v.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 truncate">
                    {doc.label}
                    {doc.type ? ` — ${doc.type}` : ""}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{doc.name}</div>
                  {doc.status && (
                    <span className="inline-flex items-center px-1.5 py-0.5 mt-1 rounded-full text-[10px] font-medium bg-slate-700/40 text-slate-300 border border-slate-600/40">
                      {doc.status}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDownload(doc)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
};

// ---------- Purchase view ----------
const PurchaseView = ({ invoices, extra, prices }) => {
  const sorted = (invoices || []).slice().sort((a, b) => (a.invoice_number ?? 0) - (b.invoice_number ?? 0));
  return (
    <Section icon={<Receipt className="h-4 w-4" />} title={`Purchase details (${sorted.length} invoice${sorted.length === 1 ? "" : "s"})`}>
      {sorted.length === 0 ? (
        <p className="text-sm text-slate-400">No invoices recorded yet.</p>
      ) : (
        <div className="space-y-3">
          {sorted.map((inv) => {
            const items = deriveInvoiceItems(inv, extra, prices);
            const stripeAmount = inv.stripe_amount ? Number(inv.stripe_amount) / 100 : null;
            const total = stripeAmount !== null ? stripeAmount : Number(inv.price || 0);
            const currency = inv.stripe_currency || "USD";
            return (
              <div
                key={inv.id}
                className="rounded-xl border border-slate-700/40 bg-slate-900/40 overflow-hidden"
              >
                <header className="flex items-center justify-between px-4 py-2 bg-slate-900/60 border-b border-slate-700/40 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Invoice</span>
                    <span className="font-mono text-slate-100">#{inv.invoice_number ?? inv.id}</span>
                    {inv.phase && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-700/40 text-slate-300 border border-slate-600/40">
                        {inv.phase}
                      </span>
                    )}
                  </div>
                  <span className="text-slate-400">
                    {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : "—"}
                  </span>
                </header>
                <div className="p-4">
                  {items.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">No line items recorded.</p>
                  ) : (
                    <ul className="divide-y divide-slate-700/40 text-sm">
                      {items.map((it, i) => (
                        <li key={i} className="flex justify-between gap-3 py-1.5">
                          <span className="text-slate-200">{it.label}</span>
                          <span className={`font-mono tabular-nums ${it.price < 0 ? "text-rose-300" : "text-slate-300"}`}>
                            {moneyFormat(it.price, currency)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-3 pt-3 border-t border-slate-700/40 flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-wider text-slate-400">Total</span>
                    <span className="text-lg font-bold text-slate-100">
                      {moneyFormat(total, currency)}
                    </span>
                  </div>
                  {inv.stripe_intent && (
                    <div className="mt-2 text-[11px] text-slate-500 font-mono truncate">
                      Stripe intent: {inv.stripe_intent}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
};

// ---------- Edit billing view ----------
const billingInitialFromExtra = (extra) => {
  const initial = {};
  const fromPrimary = !!extra?.same_as_address;
  BILLING_FIELDS.forEach((f) => {
    const primaryKey = f.key.replace(/^billing_/, "");
    initial[f.key] = (fromPrimary ? extra?.[primaryKey] : extra?.[f.key]) ?? "";
  });
  return initial;
};

const BillingView = ({ basic, extra, onSaved }) => {
  const [form, setForm] = useState(() => billingInitialFromExtra(extra));
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratedAt, setRegeneratedAt] = useState(null);

  useEffect(() => {
    setForm(billingInitialFromExtra(extra));
  }, [extra]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${base_url}/api/admin/update_billing`, {
        id: basic.id,
        ...form,
      });
      onSaved?.(response.data);
      setSavedAt(new Date());
      setRegeneratedAt(null);
    } catch (err) {
      console.error("Error saving billing info:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${base_url}/api/admin/regenerate_invoices`, {
        id: basic.id,
      });
      onSaved?.(response.data);
      setRegeneratedAt(new Date());
    } catch (err) {
      console.error("Error regenerating invoices:", err);
    } finally {
      setRegenerating(false);
    }
  };

  const handleReset = () => {
    setForm(billingInitialFromExtra(extra));
  };

  return (
    <Section icon={<Pencil className="h-4 w-4" />} title="Edit billing information">
      <div className="grid grid-cols-2 gap-3">
        {BILLING_FIELDS.map((f) => (
          <label
            key={f.key}
            className={`flex flex-col gap-1 ${f.col === 2 ? "col-span-2" : ""}`}
          >
            <span className="text-[11px] uppercase tracking-wider text-slate-400">{f.label}</span>
            <input
              type="text"
              value={form[f.key] ?? ""}
              onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
              className="bg-slate-900 border border-slate-700/60 rounded-md px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </label>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-slate-700/40 flex items-center justify-end gap-2">
        {savedAt && !saving && (
          <span className="text-[11px] text-emerald-300 mr-2">
            Saved at {savedAt.toLocaleTimeString()}
          </span>
        )}
        <button
          onClick={handleReset}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-600/30 text-slate-200 border border-slate-600/40 hover:bg-slate-600/50 disabled:opacity-60 transition-colors"
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 disabled:opacity-60 transition-colors"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {savedAt && (
        <div className="mt-3 pt-3 border-t border-slate-700/40 flex items-center justify-between gap-2">
          <p className="text-xs text-slate-400">
            Billing info updated. Regenerate the invoices to reflect the new details.
          </p>
          <div className="flex items-center gap-2">
            {regeneratedAt && !regenerating && (
              <span className="text-[11px] text-emerald-300">
                Regenerated at {regeneratedAt.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-500/20 text-amber-200 border border-amber-500/30 hover:bg-amber-500/30 disabled:opacity-60 transition-colors"
            >
              {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Receipt className="h-3.5 w-3.5" />}
              {regenerating ? "Regenerating…" : "Regenerate invoices"}
            </button>
          </div>
        </div>
      )}
    </Section>
  );
};

// ---------- Main detail modal ----------
const RegistrationDetail = ({ open, loading, data, onClose }) => {
  const [stdata, setStData] = useState(data);
  const [basic, setBasic] = useState({});
  const [extra, setExtra] = useState({});
  const [user, setUser] = useState({});
  const [enterPaper, setEnterPaper] = useState(false);
  const [loadingEnabledButton, setLoadingEnabledButton] = useState(false);
  const [paperId, setPaperId] = useState(null);
  const [, setAppliyingPaper] = useState(false);
  const [view, setView] = useState("overview");
  const [prices, setPrices] = useState(null);
  const { permissions } = useContext(RegistrationContext);

  useEffect(() => {
    if (prices) return;
    axios
      .get(`${base_url}/api/get_prices`)
      .then((res) => setPrices(res.data ?? {}))
      .catch((err) => console.error("Failed to load prices:", err));
  }, [prices]);

  useEffect(() => {
    if (stdata && stdata.data) {
      setBasic(stdata.data);
      setExtra(stdata.data.extra_registration_i_j_c_a_i25 || {});
      setUser(stdata.data.user || {});
    }
  }, [stdata]);

  useEffect(() => {
    setStData(data);
  }, [data]);

  // Reset to overview each time the modal is opened with new data
  useEffect(() => {
    if (open) setView("overview");
  }, [open, data]);

  if (!open) return null;
  if (loading) return <Loading onClose={onClose} />;

  const enableUpdates = async (id, value) => {
    setLoadingEnabledButton(true);
    try {
      const response = await axios.post(`${base_url}/api/admin/enable_updates`, {
        id,
        value,
      });
      setStData(response.data);
    } catch (error) {
      console.error("Error enabling updates:", error);
    } finally {
      setLoadingEnabledButton(false);
    }
  };

  const handleSetPaperId = async () => {
    setAppliyingPaper(true);
    try {
      const response = await axios.post(`${base_url}/api/admin/add_paper`, {
        registration_id: basic.id,
        paper_id: paperId,
      });
      setStData(response.data);
    } catch (error) {
      console.error("Error setting paper id:", error);
    } finally {
      setAppliyingPaper(false);
      setEnterPaper(false);
    }
  };

  const is_complete = basic.max_step === "Confirmation";
  const updatesEnabled = extra.update_enabled !== 0;
  const tchip = typeChip(extra.type);
  const documents = basic.documents || [];
  const invoices = extra.invoices || [];

  return (
    <Layout onClose={onClose}>
      <div className="flex flex-col h-full">
        <header className="px-5 py-3 border-b border-slate-700/40 bg-slate-900/40 flex items-center gap-3 pr-12">
          <Ticket className="h-5 w-5 text-blue-400 shrink-0" />
          <div className="flex flex-col min-w-0">
            <h2 className="text-base font-semibold text-slate-100 leading-tight truncate">
              {extra.first_name || extra.family_name
                ? `${extra.first_name ?? ""} ${extra.family_name ?? ""}`.trim()
                : "Registration details"}
            </h2>
            <span className="text-xs text-slate-400 truncate">{user.email}</span>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-200 border border-slate-600/40">
              {basic.registration_id}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${tchip.cls}`}>
              {tchip.label}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                is_complete
                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                  : "bg-amber-500/15 text-amber-300 border-amber-500/30"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  is_complete ? "bg-emerald-400" : "bg-amber-400"
                }`}
              />
              {is_complete ? "Complete" : "In progress"}
            </span>
          </div>
        </header>

        <div className="flex-1 flex min-h-0">
          {/* Left action column */}
          <aside className="w-44 shrink-0 border-r border-slate-700/40 bg-slate-900/30 p-3 flex flex-col gap-1.5 overflow-y-auto">
            <RailButton
              icon={<LayoutGrid className="h-3.5 w-3.5" />}
              label="Overview"
              active={view === "overview"}
              accent="slate"
              onClick={() => setView("overview")}
            />
            <RailButton
              icon={<Folder className="h-3.5 w-3.5" />}
              label={`Documents${documents.length ? ` (${documents.length})` : ""}`}
              active={view === "documents"}
              accent="blue"
              onClick={() => setView("documents")}
            />
            <RailButton
              icon={<Receipt className="h-3.5 w-3.5" />}
              label={`Purchase${invoices.length ? ` (${invoices.length})` : ""}`}
              active={view === "purchase"}
              accent="emerald"
              onClick={() => setView("purchase")}
            />
            <RailButton
              icon={<Pencil className="h-3.5 w-3.5" />}
              label="Edit billing"
              active={view === "billing"}
              accent="amber"
              onClick={() => setView("billing")}
            />

            <div className="mt-2 pt-2 border-t border-slate-700/40">
              {is_complete && loadingEnabledButton && (
                <RailButton
                  icon={<Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  label="Processing…"
                  disabled
                />
              )}
              {is_complete && !loadingEnabledButton && !updatesEnabled && (
                <RailButton
                  icon={<Unlock className="h-3.5 w-3.5" />}
                  label="Enable updates"
                  onClick={() => enableUpdates(basic.id, 1)}
                />
              )}
              {is_complete && !loadingEnabledButton && updatesEnabled && (
                <RailButton
                  icon={<Lock className="h-3.5 w-3.5" />}
                  label="Disable updates"
                  onClick={() => enableUpdates(basic.id, 0)}
                />
              )}
              {is_complete && permissions.includes("ijcai_add_paper") && (
                <div className="mt-1.5">
                  {enterPaper ? (
                    <div className="flex flex-col gap-1.5">
                      <input
                        className="bg-slate-900 border border-slate-700/60 rounded-md px-2 py-1 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        placeholder="Paper ID"
                        onChange={(e) => setPaperId(e.target.value)}
                      />
                      <div className="flex gap-1.5">
                        <button
                          onClick={handleSetPaperId}
                          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
                        >
                          <Check className="h-3 w-3" />
                          OK
                        </button>
                        <button
                          onClick={() => setEnterPaper(false)}
                          className="inline-flex items-center justify-center h-6 w-6 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700/40 transition-colors"
                          aria-label="Cancel"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <RailButton
                      icon={<Plus className="h-3.5 w-3.5" />}
                      label="Add paper"
                      accent="violet"
                      onClick={() => setEnterPaper(true)}
                    />
                  )}
                </div>
              )}
            </div>
          </aside>

          {/* Right pane — content switches with view */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {view === "overview" && (
              <OverviewView basic={basic} extra={extra} user={user} />
            )}
            {view === "documents" && <DocumentsView documents={documents} />}
            {view === "purchase" && (
              <PurchaseView invoices={invoices} extra={extra} prices={prices} />
            )}
            {view === "billing" && (
              <BillingView
                basic={basic}
                extra={extra}
                onSaved={(payload) => setStData(payload)}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RegistrationDetail;
